import React, { Component } from 'react';
import { X, Heart, MessageCircle, Share2, ThumbsDown, Eye, Download, FileText, Tag } from 'lucide-react';
import apiService from '@/services/api';
import styles from './PostFullscreenModal.module.scss';

interface Post {
  id: string;
  title?: string;
  description?: string;
  contentText?: string;
  postType: string;
  attachments?: any[];
  author: any;
  likesCount: number;
  dislikesCount: number;
  commentsCount: number;
  sharesCount: number;
  viewsCount: number;
  isLiked: boolean;
  isDisliked: boolean;
  createdAt: string;
  publishedAt?: string;
  tags?: string[];
}

interface PostFullscreenModalProps {
  post: Post;
  onClose: () => void;
  onLike?: (postId: string) => void;
  onDislike?: (postId: string) => void;
  onComment?: (postId: string) => void;
  onShare?: (postId: string) => void;
}

interface PostFullscreenModalState {
  isLiked: boolean;
  isDisliked: boolean;
  likesCount: number;
  dislikesCount: number;
  isProcessing: boolean;
}

export class PostFullscreenModal extends Component<PostFullscreenModalProps, PostFullscreenModalState> {
  constructor(props: PostFullscreenModalProps) {
    super(props);
    this.state = {
      isLiked: props.post.isLiked || false,
      isDisliked: props.post.isDisliked || false,
      likesCount: props.post.likesCount || 0,
      dislikesCount: props.post.dislikesCount || 0,
      isProcessing: false,
    };
  }

  componentDidMount() {
    // Track fullscreen view
    this.trackFullscreenView();
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  componentWillUnmount() {
    // Restore body scroll
    document.body.style.overflow = 'unset';
  }

  trackFullscreenView = async () => {
    try {
      await apiService.trackPostView(this.props.post.id, {
        interactionType: 'fullscreen',
      });
    } catch (error) {
      console.error('Failed to track fullscreen view:', error);
    }
  };

  handleLike = async () => {
    if (this.state.isProcessing) return;

    this.setState({ isProcessing: true });
    const wasLiked = this.state.isLiked;
    const wasDisliked = this.state.isDisliked;

    this.setState({
      isLiked: !wasLiked,
      isDisliked: false,
      likesCount: wasLiked ? this.state.likesCount - 1 : this.state.likesCount + 1,
      dislikesCount: wasDisliked ? this.state.dislikesCount - 1 : this.state.dislikesCount,
    });

    try {
      if (wasLiked) {
        await apiService.unlikePost(this.props.post.id);
      } else {
        await apiService.likePost(this.props.post.id);
        if (wasDisliked) {
          await apiService.undislikePost(this.props.post.id);
        }
      }
      if (this.props.onLike) {
        this.props.onLike(this.props.post.id);
      }
    } catch (error) {
      this.setState({
        isLiked: wasLiked,
        isDisliked: wasDisliked,
        likesCount: this.props.post.likesCount || 0,
        dislikesCount: this.props.post.dislikesCount || 0,
      });
      console.error('Failed to like post:', error);
    } finally {
      this.setState({ isProcessing: false });
    }
  };

  handleDislike = async () => {
    if (this.state.isProcessing) return;

    this.setState({ isProcessing: true });
    const wasDisliked = this.state.isDisliked;
    const wasLiked = this.state.isLiked;

    this.setState({
      isDisliked: !wasDisliked,
      isLiked: false,
      dislikesCount: wasDisliked ? this.state.dislikesCount - 1 : this.state.dislikesCount + 1,
      likesCount: wasLiked ? this.state.likesCount - 1 : this.state.likesCount,
    });

    try {
      if (wasDisliked) {
        await apiService.undislikePost(this.props.post.id);
      } else {
        await apiService.dislikePost(this.props.post.id);
        if (wasLiked) {
          await apiService.unlikePost(this.props.post.id);
        }
      }
      if (this.props.onDislike) {
        this.props.onDislike(this.props.post.id);
      }
    } catch (error) {
      this.setState({
        isLiked: wasLiked,
        isDisliked: wasDisliked,
        likesCount: this.props.post.likesCount || 0,
        dislikesCount: this.props.post.dislikesCount || 0,
      });
      console.error('Failed to dislike post:', error);
    } finally {
      this.setState({ isProcessing: false });
    }
  };

  handleShare = async () => {
    try {
      await apiService.sharePost(this.props.post.id);
      if (this.props.onShare) {
        this.props.onShare(this.props.post.id);
      }
      
      if (navigator.share) {
        const shareData = {
          title: this.props.post.title || 'Check out this post',
          text: this.props.post.description || this.props.post.contentText || '',
          url: window.location.href,
        };
        await navigator.share(shareData);
      }
    } catch (error) {
      console.error('Failed to share post:', error);
    }
  };

  formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  renderAttachment = (attachment: any) => {
    switch (attachment.attachmentType) {
      case 'image':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <img
              src={attachment.fileUrl || attachment.thumbnailUrl}
              alt={attachment.fileName || 'Post image'}
              className={styles.attachmentImage}
            />
          </div>
        );
      
      case 'video':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <video
              src={attachment.fileUrl}
              controls
              className={styles.attachmentVideo}
              poster={attachment.thumbnailUrl}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        );
      
      case 'gif':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <img
              src={attachment.fileUrl || attachment.thumbnailUrl}
              alt={attachment.fileName || 'GIF'}
              className={styles.attachmentImage}
            />
          </div>
        );
      
      case 'file':
      case 'pdf':
      case 'word':
        return (
          <div key={attachment.id} className={styles.fileAttachment}>
            <div className={styles.fileIcon}>
              <FileText size={32} />
            </div>
            <div className={styles.fileInfo}>
              <span className={styles.fileName}>{attachment.fileName || 'File'}</span>
              {attachment.fileSize && (
                <span className={styles.fileSize}>{this.formatFileSize(attachment.fileSize)}</span>
              )}
            </div>
            {attachment.fileUrl && (
              <a
                href={attachment.fileUrl}
                download
                className={styles.downloadButton}
                title="Download file"
              >
                <Download size={20} />
              </a>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  render() {
    const { post, onClose, onComment } = this.props;
    const { isLiked, isDisliked, likesCount, dislikesCount } = this.state;

    return (
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <button className={styles.closeButton} onClick={onClose} title="Close">
            <X size={24} />
          </button>

          <div className={styles.modalBody}>
            {/* Author Info */}
            <div className={styles.authorSection}>
              <img
                src={post.author.profilePic || '/default-avatar.png'}
                alt={post.author.displayName}
                className={styles.authorAvatar}
              />
              <div className={styles.authorDetails}>
                <span className={styles.authorName}>{post.author.displayName}</span>
                <span className={styles.postDate}>
                  {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString() : new Date(post.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Title */}
            {post.title && (
              <h2 className={styles.postTitle}>{post.title}</h2>
            )}

            {/* Description */}
            {post.description && (
              <p className={styles.postDescription}>{post.description}</p>
            )}

            {/* Content */}
            {post.contentText && (
              <div className={styles.postContent}>
                <p>{post.contentText}</p>
              </div>
            )}

            {/* Attachments */}
            {post.attachments && post.attachments.length > 0 && (
              <div className={styles.attachmentsContainer}>
                {post.attachments.map(attachment => this.renderAttachment(attachment))}
              </div>
            )}

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className={styles.postTags}>
                <Tag size={18} className={styles.tagsIcon} />
                <div className={styles.tagsList}>
                  {post.tags.map((tag, index) => (
                    <span key={index} className={styles.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Stats */}
            <div className={styles.postStats}>
              <span className={styles.statItem}>
                <Eye size={18} />
                {post.viewsCount.toLocaleString()} views
              </span>
              <span className={styles.statItem}>
                <Heart size={18} />
                {likesCount.toLocaleString()} likes
              </span>
              {dislikesCount > 0 && (
                <span className={styles.statItem}>
                  <ThumbsDown size={18} />
                  {dislikesCount.toLocaleString()} dislikes
                </span>
              )}
              <span className={styles.statItem}>
                <MessageCircle size={18} />
                {post.commentsCount.toLocaleString()} comments
              </span>
            </div>

            {/* Actions */}
            <div className={styles.postActions}>
              <button
                className={`${styles.actionButton} ${isLiked ? styles.active : ''}`}
                onClick={this.handleLike}
                disabled={this.state.isProcessing}
              >
                <Heart size={22} fill={isLiked ? 'currentColor' : 'none'} />
                <span>Like</span>
              </button>
              <button
                className={`${styles.actionButton} ${isDisliked ? styles.active : ''}`}
                onClick={this.handleDislike}
                disabled={this.state.isProcessing}
              >
                <ThumbsDown size={22} fill={isDisliked ? 'currentColor' : 'none'} />
                <span>Dislike</span>
              </button>
              <button
                className={styles.actionButton}
                onClick={() => onComment && onComment(post.id)}
              >
                <MessageCircle size={22} />
                <span>Comment</span>
              </button>
              <button
                className={styles.actionButton}
                onClick={this.handleShare}
              >
                <Share2 size={22} />
                <span>Share</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default PostFullscreenModal;

